import React, { useState, useEffect } from "react";
import { createComment, Comment } from "../api-requests/Comments";
import { VerifyToken } from "../api-requests/Get";
import {
  loadRecaptchaScript,
  unloadRecaptchaScript,
} from "../helpers/Recapture";
import { config } from "../config/config";

declare const grecaptcha: any;

interface CommentComposerProps {
  targetType: "debate" | "list" | "list_item";
  targetId: string;
  parentId?: string;
  onCommentAdded: (comment: Comment) => void;
  placeholder?: string;
  buttonText?: string;
  className?: string;
  autoFocus?: boolean;
  onCancel?: () => void;
}

const CommentComposer: React.FC<CommentComposerProps> = ({
  targetType,
  targetId,
  parentId,
  onCommentAdded,
  placeholder = "Write a comment...",
  buttonText = "Comment",
  className = "",
  autoFocus = false,
  onCancel,
}) => {
  const [body, setBody] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const siteKey = config.RECAPTURE_SITE_KEY;

  useEffect(() => {
    if (siteKey) {
      loadRecaptchaScript(siteKey)
        .then(() => {
          console.log("reCAPTCHA script loaded for comments");
        })
        .catch((err) => {
          console.error("Failed to load reCAPTCHA script:", err);
        });
    }

    return () => {
      unloadRecaptchaScript();
    };
  }, [siteKey]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!body.trim()) {
      setError("Comment cannot be empty");
      return;
    }

    if (body.length > 1000) {
      setError("Comment cannot exceed 1000 characters");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // reCAPTCHA verification
      if (window.grecaptcha && siteKey) {
        const token = await window.grecaptcha.execute(siteKey);
        const isValid = await VerifyToken(token);
        if (!isValid) {
          setError("Security verification failed. Please try again.");
          setIsSubmitting(false);
          return;
        }
      } else {
        console.warn("reCAPTCHA not available for comment submission");
      }

      const response = await createComment(
        targetType,
        targetId,
        body.trim(),
        parentId,
      );

      if (response.success && response.comment) {
        setBody("");
        onCommentAdded(response.comment);
        onCancel?.(); // Call onCancel if provided (for reply forms)
      } else {
        setError(response.message || "Failed to post comment");
      }
    } catch (err) {
      setError("An error occurred while posting your comment");
      console.error("Error creating comment:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setBody("");
    setError(null);
    onCancel?.();
  };

  const remainingChars = 1000 - body.length;
  const isNearLimit = remainingChars < 100;
  const isOverLimit = remainingChars < 0;

  return (
    <form onSubmit={handleSubmit} className={`space-y-3 ${className}`}>
      {/* Text Area */}
      <div className="relative">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={isSubmitting}
          rows={parentId ? 3 : 4} // Smaller for replies
          className={`w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 resize-vertical min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
            isSubmitting ? "opacity-50 cursor-not-allowed" : ""
          } ${isOverLimit ? "border-red-500 focus:ring-red-500" : ""}`}
        />

        {/* Character Count */}
        {(isNearLimit || body.length > 0) && (
          <div
            className={`absolute bottom-2 right-2 text-xs font-medium ${
              isOverLimit
                ? "text-red-400"
                : isNearLimit
                  ? "text-yellow-400"
                  : "text-gray-500"
            }`}
          >
            {remainingChars}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-red-400 text-sm bg-red-900/20 border border-red-600 rounded px-3 py-2">
          {error}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={isSubmitting || !body.trim() || isOverLimit}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
            isSubmitting || !body.trim() || isOverLimit
              ? "bg-gray-600 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
          }`}
        >
          {isSubmitting ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              Posting...
            </div>
          ) : (
            buttonText
          )}
        </button>

        {/* Cancel Button (for replies) */}
        {onCancel && (
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Guidelines (only for top-level comments) */}
      {!parentId && (
        <div className="text-xs text-gray-500 space-y-1">
          <p>Guidelines: Be respectful, stay on topic, and keep it civil.</p>
        </div>
      )}

      {/* reCAPTCHA container */}
      <div id="recaptcha-container" style={{ display: "none" }}></div>
    </form>
  );
};

export default CommentComposer;
